import React from 'react';
import { ShieldCheck, UserCheck, GitCompare, ClipboardList, Inbox } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  statusFromChartRows,
  sumRowValues,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import { mapChartRowsWithStatusLabels } from './approbationsShared';

export default function ApprovalsDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.approbations.pages.dashboardPage.${key}`, opts);
  const pl = (key, opts) => t(`modules.approbations.pages.common.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/approbations/dashboard"
      title={p('title', { defaultValue: 'لوحة تحكم الموافقات' })}
      subtitle={p('subtitle', { defaultValue: 'متابعة مسارات الاعتماد والطلبات' })}
      icon={ShieldCheck}
      accent="from-violet-600 to-purple-900"
      recentTitle={p('recent_requests', { defaultValue: 'آخر الطلبات' })}
      quickLinks={[
        { label: p('link_my_approvals', { defaultValue: 'موافقاتي' }), href: '/approbations/mes-approbations', icon: UserCheck },
        { label: p('link_workflows', { defaultValue: 'المسارات النشطة' }), href: '/approbations/workflows', icon: GitCompare },
        { label: p('link_create', { defaultValue: 'إنشاء مسار' }), href: '/approbations/creer', icon: ShieldCheck },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/approbations/analyses-rapports', icon: ClipboardList },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = mapChartRowsWithStatusLabels(data.chartRows || [], pl);
        const { active, inactive } = statusFromChartRows(data.chartRows || [], [
          'approuve',
          'approved',
          'accepted',
          'completed',
          'actif',
          'active',
        ]);

        return {
          hero: {
            label: p('kpi_pending', { defaultValue: 'بانتظار موافقتي' }),
            value: kpis.pendingApproval ?? 0,
            icon: UserCheck,
          },
          kpis: sliceQawaemStats(withKpiVariants([
            { label: p('kpi_workflows', { defaultValue: 'مسارات نشطة' }), value: kpis.activeWorkflows ?? 0, icon: GitCompare },
            { label: p('kpi_avg_time', { defaultValue: 'مؤشر الاعتماد' }), value: kpis.avgApprovalTime ?? 0, icon: ShieldCheck },
            { label: p('kpi_rejected', { defaultValue: 'مرفوض (أسبوع)' }), value: kpis.rejectedWeek ?? 0, icon: ClipboardList },
            {
              label: p('kpi_total_requests', { defaultValue: 'إجمالي الطلبات' }),
              value: sumRowValues(chartRows),
              icon: Inbox,
            },
          ]), 4),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_status', { defaultValue: 'حالة الطلبات' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'طلبات ومسارات ورفض' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_pending', { defaultValue: 'بانتظار موافقتي' }), value: kpis.pendingApproval ?? 0 },
                { label: p('kpi_workflows', { defaultValue: 'مسارات نشطة' }), value: kpis.activeWorkflows ?? 0 },
                { label: p('kpi_rejected', { defaultValue: 'مرفوض (أسبوع)' }), value: kpis.rejectedWeek ?? 0 },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'نتيجة الطلبات' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'موافق عليها مقابل معلقة' }),
              active: active || 0,
              inactive: inactive || kpis.pendingApproval || 0,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_ranked_title', { defaultValue: 'توزيع حالات الطلبات' }),
              subtitle: p('chart_ranked_subtitle', { defaultValue: 'حسب الحالة' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
